import React from 'react';
import { Check, Star, ArrowRight } from 'lucide-react';
import { pricingPlans } from '../mock';
import { Button } from './ui/button';

const PricingSection = ({ onOpenPopup }) => {
  return (
    <section className="py-16 bg-gradient-to-b from-white via-[#E2F8FE]/30 to-white relative overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 overflow-hidden opacity-5">
        <div className="absolute top-20 left-10 w-64 h-64 bg-[#FF4A2A] rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-[#E2F8FE] rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-block bg-[#E2F8FE] rounded-full px-6 py-2 mb-4">
            <span className="text-[#FF4A2A] font-semibold text-base">PRICING</span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Pricing Plans
          </h2>
          <p className="text-base text-gray-600 max-w-3xl mx-auto">
            Transparent pricing with no hidden fees. All plans include expert support and comprehensive protection.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {pricingPlans.map((plan, index) => (
            <div
              key={plan.id}
              className={`relative bg-white rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl flex flex-col ${
                plan.recommended
                  ? 'border-2 border-[#FF4A2A]'
                  : 'border border-gray-200'
              }`}
            >
              {/* Recommended Badge */}
              {plan.recommended && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                  <div className="bg-[#FF4A2A] text-white px-4 py-1 rounded text-xs font-bold shadow-md flex items-center gap-1">
                    <Star className="w-3 h-3 fill-white" />
                    Recommended
                  </div>
                </div>
              )}

              {/* Card Header */}
              <div className={`rounded-t-lg p-6 text-center border-b ${
                plan.recommended 
                  ? 'bg-[#FF4A2A] border-[#E63E1F]' 
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <h3 className={`text-xl font-bold mb-2 ${
                  plan.recommended ? 'text-white' : 'text-gray-900'
                }`}>
                  {plan.name}
                </h3>
                <div className="mb-2">
                  <span className={`text-4xl font-bold ${
                    plan.recommended ? 'text-white' : 'text-[#FF4A2A]'
                  }`}>
                    {plan.price}
                  </span>
                </div>
                <p className={`text-sm font-medium ${
                  plan.recommended ? 'text-white/90' : 'text-gray-600'
                }`}>
                  {plan.fee}
                </p>
              </div>

              {/* Card Body - Flex grow to push button to bottom */}
              <div className="p-6 flex flex-col flex-grow">
                {/* Features List - Scrollable with fixed height */}
                <div className="mb-5 space-y-3 max-h-[280px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 flex-grow">
                  {plan.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <div className="flex-shrink-0 mt-0.5">
                        <Check className="h-5 w-5 text-green-600 stroke-[2.5]" />
                      </div>
                      <span className="text-sm text-gray-700 leading-relaxed">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Note and Button Section - Stays at bottom */}
                <div className="mt-auto">
                  {/* Note */}
                  <div className="mb-4 p-3 bg-gray-50 rounded border border-gray-100">
                    <p className="text-xs text-gray-600 text-center">{plan.note}</p>
                  </div>

                  {/* CTA Button */}
                  <Button
                    onClick={onOpenPopup}
                    className={`w-full py-3.5 px-4 rounded-lg font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-2 ${
                      plan.recommended
                        ? 'bg-[#FF4A2A] hover:bg-[#E63E1F] text-white shadow-md hover:shadow-lg'
                        : 'bg-white border-2 border-[#FF4A2A] text-[#FF4A2A] hover:bg-[#FF4A2A] hover:text-white'
                    }`}
                  >
                    Get Started
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-10 text-center">
          <p className="text-gray-600 mb-4 text-base">
            Not sure which plan is right for you? <span className="font-semibold text-gray-900">Let us help!</span>
          </p>
          <Button
            onClick={onOpenPopup}
            className="bg-white border-2 border-[#FF4A2A] text-[#FF4A2A] hover:bg-[#FF4A2A] hover:text-white px-8 py-3 text-base font-semibold rounded-lg transition-all duration-300 shadow-md"
          >
            Schedule Free Consultation
          </Button>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;